import { Controller } from "@hotwired/stimulus"

// The panel behind every timestamp in the app: the same instant in UTC and in whatever zone the
// browser says the reader is in. The app renders UTC everywhere and labels it nowhere, so a
// timestamp read at a glance is read as local time — this is what makes the two legible side by
// side, without changing a word of what the timestamp already says.
//
// The controller sits on the single panel (see layouts/application) rather than on each <time>,
// and listens on the document. A thread view can show eighty timestamps at once, and eighty
// controller instances holding six listeners each is a real cost for a hover affordance.
const VIEWPORT_MARGIN = 8
const TRIGGER_GAP = 6
const TRIGGER_SELECTOR = "[data-time-popover]"

// Explicit components rather than dateStyle/timeStyle, and hour12 pinned on both: comparing the
// two lines IS the feature, and a 12-hour line above a 24-hour one is measurably harder to read
// against each other. Spelling the fields out makes the two format-identical by construction.
const TIME_FORMAT = {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false
}

export default class extends Controller {
  static targets = ["utc", "local", "localLabel", "zone"]

  connect() {
    // Built once and reused. Constructing an Intl.DateTimeFormat is the expensive half of
    // formatting, and this runs on every pointer crossing a list of timestamps.
    this.utcFormatter = new Intl.DateTimeFormat(undefined, { ...TIME_FORMAT, timeZone: "UTC" })
    this.localFormatter = new Intl.DateTimeFormat(undefined, TIME_FORMAT)
    this.localZone = Intl.DateTimeFormat().resolvedOptions().timeZone

    this.show = this.show.bind(this)
    this.hide = this.hide.bind(this)
    this.hideOnEscape = this.hideOnEscape.bind(this)

    // mouseover/mouseout rather than mouseenter/mouseleave: only the former bubble, and the whole
    // point of listening on the document is to serve every trigger with one pair of listeners.
    document.addEventListener("mouseover", this.show)
    document.addEventListener("mouseout", this.hide)
    document.addEventListener("focusin", this.show)
    document.addEventListener("focusout", this.hide)
    document.addEventListener("keydown", this.hideOnEscape)
    // Capturing, because the elements that scroll are .app__list, .thread__scroll and
    // .thread-info — a scroll event does not bubble out of them to the document. Without this a
    // fixed panel stays where it was painted while the timestamp it describes slides away.
    document.addEventListener("scroll", this.hide, true)
  }

  disconnect() {
    // Closes as well as unbinding, so a trigger cannot be left pointing aria-describedby at a
    // panel this controller has stopped driving.
    this.hide()
    document.removeEventListener("mouseover", this.show)
    document.removeEventListener("mouseout", this.hide)
    document.removeEventListener("focusin", this.show)
    document.removeEventListener("focusout", this.hide)
    document.removeEventListener("keydown", this.hideOnEscape)
    document.removeEventListener("scroll", this.hide, true)
  }

  show(event) {
    const trigger = event.target.closest?.(TRIGGER_SELECTOR)
    if (!trigger) return

    // Checked before the Date is built, not after: `new Date(null)` is the epoch rather than an
    // invalid date, so a trigger carrying no datetime would sail past the NaN guard below and
    // announce January 1st 1970 with total confidence.
    const stamp = trigger.getAttribute("datetime")
    if (!stamp) return

    const instant = new Date(stamp)
    if (Number.isNaN(instant.getTime())) return

    this.fill(instant)
    this.element.hidden = false
    this.position(trigger)
    this.describe(trigger)
  }

  hide() {
    if (this.element.hidden) return

    this.element.hidden = true
    this.describedTrigger?.removeAttribute("aria-describedby")
    this.describedTrigger = null
  }

  hideOnEscape(event) {
    if (event.key === "Escape") this.hide()
  }

  // Written with textContent, never innerHTML: nothing here should be able to become markup.
  fill(instant) {
    const utc = this.utcFormatter.format(instant)
    const local = this.localFormatter.format(instant)
    // A reader who IS on UTC would otherwise get the same string twice, which reads as a bug
    // rather than as agreement. Compared on the formatted strings so a zone that merely happens
    // to be at +00:00 today (Europe/London in winter) collapses too.
    const sameMoment = utc === local

    this.utcTarget.textContent = utc
    this.localTarget.textContent = local
    this.localTarget.hidden = sameMoment
    this.localLabelTarget.hidden = sameMoment
    this.zoneTarget.textContent = sameMoment
      ? `Same as your local time (${this.localZone})`
      : this.localZone
  }

  // Above the trigger by default, flipped below when there is no room. Measured after the panel
  // is shown, because a hidden element has no size to centre on.
  position(trigger) {
    const anchor = trigger.getBoundingClientRect()
    const panel = this.element.getBoundingClientRect()

    const above = anchor.top - panel.height - TRIGGER_GAP
    const top = above < VIEWPORT_MARGIN ? anchor.bottom + TRIGGER_GAP : above

    const centred = anchor.left + anchor.width / 2 - panel.width / 2
    // Clamped so a timestamp at the edge of the 300px info rail — which is where this actually
    // gets tested — keeps the whole panel on screen.
    const rightmost = window.innerWidth - panel.width - VIEWPORT_MARGIN
    const left = Math.max(VIEWPORT_MARGIN, Math.min(centred, rightmost))

    this.element.style.top = `${top}px`
    this.element.style.left = `${left}px`
  }

  // Only the message card's timestamp is focusable, and it is the one whose absolute wording can
  // be misread — so it is also the only one that needs the panel announced as its description.
  describe(trigger) {
    if (!trigger.hasAttribute("tabindex")) return

    trigger.setAttribute("aria-describedby", this.element.id)
    this.describedTrigger = trigger
  }
}
